import React, { useEffect, useState, useCallback } from 'react';
import { User } from '../../types/auth';
import { usePredictionMarket, Market } from '../../hooks/usePredictionMarket';
import { useMarketSettlement, AuditLogEntry } from '../../hooks/useMarketSettlement';
import TxToast from '../common/TxToast';

interface AdminSettlementPageProps {
  user: User | null;
}

type ActionModal = 'approve' | 'manual' | 'reject' | null;

interface BlockInfo {
  number: number | null;
  timestamp: number | null;
}

export default function AdminSettlementPage({ user }: AdminSettlementPageProps) {
  const { markets, loadingMarkets, fetchMarkets } = usePredictionMarket();
  const { tx, resetTx, auditLog, approveSettlement, manualSettlement, rejectSettlement } =
    useMarketSettlement();

  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [actionModal, setActionModal]         = useState<ActionModal>(null);
  const [blockInfo, setBlockInfo]             = useState<BlockInfo>({ number: null, timestamp: null });

  // Manual settlement form state
  const [manualOutcome, setManualOutcome]           = useState<boolean>(true);
  const [manualPrice, setManualPrice]               = useState('');
  const [manualSignature, setManualSignature]       = useState('');
  const [manualEvidenceHash, setManualEvidenceHash] = useState('');

  // Approve form state
  const [approveOutcome, setApproveOutcome]           = useState<boolean>(true);
  const [approvePrice, setApprovePrice]               = useState('');
  const [approveEvidenceHash, setApproveEvidenceHash] = useState('');

  // Reject form state
  const [rejectNote, setRejectNote] = useState('');

  const fetchBlockInfo = useCallback(async () => {
    try {
      const eth = (window as any).ethereum;
      if (!eth) return;
      const { ethers } = await import('ethers');
      const provider = new ethers.BrowserProvider(eth);
      const block = await provider.getBlock('latest');
      if (block) {
        setBlockInfo({ number: block.number, timestamp: block.timestamp });
      }
    } catch {
      // Block info is informational only; ignore errors
    }
  }, []);

  useEffect(() => {
    fetchMarkets();
    fetchBlockInfo();
  }, [fetchMarkets, fetchBlockInfo]);

  // Access guard
  if (!user?.isAdmin) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <span className="access-denied-icon">🔒</span>
          <h2>Admin Access Required</h2>
          <p>Only administrators or oracle operators may access the settlement dashboard.</p>
        </div>
      </div>
    );
  }

  const pendingMarkets = markets.filter(m => !m.resolved);
  const settledMarkets = markets.filter(m => m.resolved);

  function openModal(market: Market, action: ActionModal) {
    setSelectedMarket(market);
    setActionModal(action);
    resetTx();
    // Reset form state
    setManualOutcome(true);
    setManualPrice('');
    setManualSignature('');
    setManualEvidenceHash('');
    setApproveOutcome(true);
    setApprovePrice('');
    setApproveEvidenceHash('');
    setRejectNote('');
  }

  function closeModal() {
    setSelectedMarket(null);
    setActionModal(null);
    resetTx();
  }

  async function handleApprove(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMarket) return;
    const ok = await approveSettlement(
      selectedMarket.id,
      approveOutcome,
      approvePrice,
      approveEvidenceHash,
    );
    if (ok) {
      closeModal();
      fetchMarkets();
    }
  }

  async function handleManual(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMarket) return;
    const ok = await manualSettlement(
      selectedMarket.id,
      manualOutcome,
      manualPrice,
      manualSignature,
      manualEvidenceHash,
    );
    if (ok) {
      closeModal();
      fetchMarkets();
    }
  }

  function handleReject(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMarket) return;
    rejectSettlement(selectedMarket.id, rejectNote);
    closeModal();
  }

  function formatTs(ts: number) {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleString();
  }

  function formatAuditAction(action: AuditLogEntry['action']) {
    if (action === 'approve') return '✅ Approved';
    if (action === 'manual')  return '✍️ Manual';
    return '🚫 Rejected';
  }

  const contractAddr = process.env.REACT_APP_SETTLEMENT_CONTRACT_ADDRESS;

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">🛡️ Settlement Review Dashboard</h2>
        <p className="page-subtitle">
          Monitor pending markets, approve or manually submit settlements, and log challenges.
        </p>
      </div>

      {/* Status bar */}
      <div className="settlement-status-bar">
        <span className="settlement-status-item">
          📦 Block: <strong>{blockInfo.number ?? '—'}</strong>
        </span>
        <span className="settlement-status-item">
          🕒 Block time: <strong>{blockInfo.timestamp ? formatTs(blockInfo.timestamp) : '—'}</strong>
        </span>
        <span className="settlement-status-item">
          ⏳ Pending: <strong>{pendingMarkets.length}</strong>
        </span>
        <span className="settlement-status-item">
          ✅ Settled: <strong>{settledMarkets.length}</strong>
        </span>
        {contractAddr && (
          <span className="settlement-status-item settlement-contract-addr">
            📜 Contract:{' '}
            <a
              href={`https://sepolia.etherscan.io/address/${contractAddr}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-toast-link"
            >
              {contractAddr.slice(0, 8)}…{contractAddr.slice(-6)}
            </a>
          </span>
        )}
        <button
          className="btn-outline"
          onClick={() => { fetchMarkets(); fetchBlockInfo(); }}
          disabled={loadingMarkets}
          style={{ marginLeft: 'auto' }}
        >
          {loadingMarkets ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      {/* Pending Markets */}
      <section className="settlement-section">
        <h3 className="section-title">Pending Markets</h3>
        {loadingMarkets ? (
          <div className="loading-state">Loading markets…</div>
        ) : pendingMarkets.length === 0 ? (
          <div className="empty-state">No markets pending resolution. ✓</div>
        ) : (
          <div className="settlement-grid">
            {pendingMarkets.map(m => {
              const isExpired = m.deadline > 0 && Date.now() / 1000 > m.deadline;
              return (
                <div key={m.id} className={`settlement-card${isExpired ? ' settlement-card-expired' : ''}`}>
                  <div className="settlement-card-header">
                    <span className="market-id">#{m.id}</span>
                    {isExpired ? (
                      <span className="badge badge-expired">Expired – ready</span>
                    ) : (
                      <span className="badge badge-open">Active</span>
                    )}
                  </div>

                  <p className="market-question">{m.question}</p>

                  <div className="settlement-card-details">
                    <div className="settlement-detail">
                      <span className="settlement-detail-label">YES pool</span>
                      <span className="settlement-detail-value">
                        {parseFloat(m.yesPool).toFixed(4)} ETH
                      </span>
                    </div>
                    <div className="settlement-detail">
                      <span className="settlement-detail-label">NO pool</span>
                      <span className="settlement-detail-value">
                        {parseFloat(m.noPool).toFixed(4)} ETH
                      </span>
                    </div>
                    {m.deadline > 0 && (
                      <div className="settlement-detail">
                        <span className="settlement-detail-label">Deadline</span>
                        <span className="settlement-detail-value">{formatTs(m.deadline)}</span>
                      </div>
                    )}
                    <div className="settlement-detail">
                      <span className="settlement-detail-label">Total pool</span>
                      <span className="settlement-detail-value">
                        {(parseFloat(m.yesPool) + parseFloat(m.noPool)).toFixed(4)} ETH
                      </span>
                    </div>
                  </div>

                  <div className="settlement-card-actions">
                    <button
                      className="btn-primary"
                      onClick={() => openModal(m, 'approve')}
                      title="Approve a bot-submitted or pre-verified settlement"
                    >
                      ✅ Approve Settlement
                    </button>
                    <button
                      className="btn-outline"
                      onClick={() => openModal(m, 'manual')}
                      title="Manually set outcome and submit to contract"
                    >
                      ✍️ Manual Submit
                    </button>
                    <button
                      className="btn-outline btn-danger-outline"
                      onClick={() => openModal(m, 'reject')}
                      title="Log a rejection or challenge for audit purposes"
                    >
                      🚫 Reject / Challenge
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Settled Markets */}
      {settledMarkets.length > 0 && (
        <section className="settlement-section">
          <h3 className="section-title">Settled Markets</h3>
          <div className="settlement-grid">
            {settledMarkets.map(m => (
              <div key={m.id} className="settlement-card settlement-card-done">
                <div className="settlement-card-header">
                  <span className="market-id">#{m.id}</span>
                  <span className={`badge ${m.outcome ? 'badge-yes' : 'badge-no'}`}>
                    {m.outcome ? 'YES ✓' : 'NO ✗'}
                  </span>
                </div>
                <p className="market-question">{m.question}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Audit Log */}
      {auditLog.length > 0 && (
        <section className="settlement-section">
          <h3 className="section-title">📋 Audit Log (Session)</h3>
          <div className="audit-log">
            {auditLog.map((entry, idx) => (
              <div key={idx} className="audit-entry">
                <span className="audit-time">{entry.timestamp.toLocaleTimeString()}</span>
                <span className="audit-action">{formatAuditAction(entry.action)}</span>
                <span className="audit-market">Market #{entry.marketId}</span>
                {entry.outcome !== undefined && (
                  <span className="audit-outcome">→ {entry.outcome ? 'YES' : 'NO'}</span>
                )}
                {entry.note && <span className="audit-note">"{entry.note}"</span>}
                {entry.txHash && (
                  <a
                    className="tx-toast-link"
                    href={`https://sepolia.etherscan.io/tx/${entry.txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Tx ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Modals ── */}

      {selectedMarket && actionModal === 'approve' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">✅ Approve Settlement – Market #{selectedMarket.id}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <p className="modal-market-question">{selectedMarket.question}</p>
            <TxToast tx={tx} onDismiss={resetTx} />
            <form onSubmit={handleApprove} noValidate>
              <div className="form-group">
                <label className="form-label">Outcome</label>
                <div className="prediction-toggle">
                  <button
                    type="button"
                    className={`toggle-btn toggle-yes${approveOutcome ? ' active' : ''}`}
                    onClick={() => setApproveOutcome(true)}
                  >
                    YES – Condition Met
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn toggle-no${!approveOutcome ? ' active' : ''}`}
                    onClick={() => setApproveOutcome(false)}
                  >
                    NO – Condition Not Met
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="approve-price">
                  Settlement Price (wei, optional)
                </label>
                <input
                  id="approve-price"
                  className="form-input"
                  type="text"
                  placeholder="e.g. 300000000000 (leave blank for 0)"
                  value={approvePrice}
                  onChange={e => setApprovePrice(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="approve-evidence">
                  Evidence Hash (optional)
                </label>
                <input
                  id="approve-evidence"
                  className="form-input"
                  type="text"
                  placeholder="IPFS CID or keccak256 hash"
                  value={approveEvidenceHash}
                  onChange={e => setApproveEvidenceHash(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="btn-primary btn-full"
                disabled={tx.status === 'pending'}
              >
                {tx.status === 'pending' ? '⏳ Submitting…' : `Approve as ${approveOutcome ? 'YES' : 'NO'}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {selectedMarket && actionModal === 'manual' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">✍️ Manual Settlement – Market #{selectedMarket.id}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <p className="modal-market-question">{selectedMarket.question}</p>
            <TxToast tx={tx} onDismiss={resetTx} />
            <form onSubmit={handleManual} noValidate>
              <div className="form-group">
                <label className="form-label">Outcome</label>
                <div className="prediction-toggle">
                  <button
                    type="button"
                    className={`toggle-btn toggle-yes${manualOutcome ? ' active' : ''}`}
                    onClick={() => setManualOutcome(true)}
                  >
                    YES – Condition Met
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn toggle-no${!manualOutcome ? ' active' : ''}`}
                    onClick={() => setManualOutcome(false)}
                  >
                    NO – Condition Not Met
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="manual-price">
                  Settlement Price (wei)
                </label>
                <input
                  id="manual-price"
                  className="form-input"
                  type="text"
                  placeholder="e.g. 300000000000 (leave blank for 0)"
                  value={manualPrice}
                  onChange={e => setManualPrice(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="manual-sig">
                  Oracle Signature (optional)
                </label>
                <input
                  id="manual-sig"
                  className="form-input"
                  type="text"
                  placeholder="0x… ECDSA signature from oracle key"
                  value={manualSignature}
                  onChange={e => setManualSignature(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="manual-evidence">
                  Evidence Hash (optional)
                </label>
                <input
                  id="manual-evidence"
                  className="form-input"
                  type="text"
                  placeholder="IPFS CID or keccak256 hash"
                  value={manualEvidenceHash}
                  onChange={e => setManualEvidenceHash(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="btn-primary btn-full btn-danger"
                disabled={tx.status === 'pending'}
              >
                {tx.status === 'pending' ? '⏳ Submitting…' : `Submit as ${manualOutcome ? 'YES' : 'NO'}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {selectedMarket && actionModal === 'reject' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🚫 Reject / Challenge – Market #{selectedMarket.id}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <p className="modal-market-question">{selectedMarket.question}</p>
            <p className="modal-info">
              This action logs the rejection for audit purposes only and does not submit a transaction.
            </p>
            <form onSubmit={handleReject} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="reject-note">
                  Reason / Note
                </label>
                <textarea
                  id="reject-note"
                  className="form-input form-textarea"
                  placeholder="Describe why the proposed settlement is being rejected or challenged…"
                  value={rejectNote}
                  onChange={e => setRejectNote(e.target.value)}
                  rows={4}
                />
              </div>
              <button
                type="submit"
                className="btn-danger btn-full"
                disabled={!rejectNote.trim()}
              >
                Log Rejection
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
